import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, Loader2, Trophy, Clock, Wallet as WalletIcon, Zap, Crown, Star,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Daily Spin Wheel — Iter-37 premium redesign.
 *
 * Goals of this rebuild:
 *  - Cinematic staging: aura rings, sparkle particles, gold shimmer,
 *    scanline overlay + grain — matches the /info/team + /info + /tools
 *    treatment used across the site.
 *  - "Mystic operator" copy — every reload picks a new incantation from a
 *    rotating pool so the page feels alive on repeat visits.
 *  - Prize preview strip so users see WHAT they can win before spinning.
 *  - Big win reveal: confetti burst + gold ring + trophy pop.
 */

const TWO_PI = Math.PI * 2;

const HOOK_LINES = [
  'roll the underground. the wheel remembers you.',
  'one spin a day. sometimes fortune. sometimes fate.',
  'the operators\' oracle. calibrated. cursed. free.',
  'every spin is signed. every prize is real.',
  'twenty-four hours between blessings. spin wisely.',
  'the code decides. the vault delivers. instantly.',
];

const MYSTIC_STRIP = ['◊', '△', '◈', '⬢', '❖', '✦', '◇', '❋', '⬡'];

const SpinWheel = () => {
  const { user, loading } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winning, setWinning] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [burst, setBurst] = useState(false);   // confetti trigger
  const wheelRef = useRef(null);

  const hook = useMemo(() => HOOK_LINES[Math.floor(Math.random() * HOOK_LINES.length)], []);

  const reload = async () => {
    try {
      const c = await api.spinConfig();
      setCfg(c);
      if (user) setStatus(await api.spinStatus());
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); }, [user]);

  useEffect(() => {
    if (!status?.next_at) { setTimeLeft(''); return; }
    const tick = () => {
      const diff = new Date(status.next_at).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft(''); reload(); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [status?.next_at]);

  if (loading) return <section className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[var(--eh-green)]" /></section>;
  if (!user) return <section className="min-h-[60vh] grid place-items-center"><Link to="/login" className="eh-btn-primary">Sign in to play</Link></section>;
  if (!cfg) return <section className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[var(--eh-green)]" /></section>;
  if (!cfg.enabled) return <section className="min-h-[60vh] grid place-items-center text-center"><div><h1 className="eh-display text-2xl font-black mb-2">The wheel sleeps tonight</h1><p className="eh-mono text-xs opacity-60">Return by moonrise — the operators are recalibrating.</p></div></section>;

  const prizes = cfg.prizes || [];
  const slice = TWO_PI / prizes.length;

  const handleSpin = async () => {
    if (spinning || !status?.can_spin) return;
    setSpinning(true);
    setWinning(null);
    setBurst(false);
    try {
      const res = await api.spinSpin();
      const idx = prizes.findIndex(p => p.id === res.prize.id);
      const target = -(idx + 0.5) * slice;
      const fullTurns = 6 * TWO_PI;
      const final = (rotation || 0) % TWO_PI - ((rotation || 0) % TWO_PI) + fullTurns + target;
      setRotation(final);
      setTimeout(() => {
        setWinning(res.prize);
        setSpinning(false);
        reload();
        if (res.prize.type === 'credit' && res.prize.amount > 0) {
          setBurst(true);
          setTimeout(() => setBurst(false), 2600);
          toast.success(`Won ${res.prize.label}!`, { description: 'Credited to your wallet · instantly.' });
        } else {
          toast.message(res.prize.label, { description: 'Not this cycle — the wheel remembers though.' });
        }
      }, 3700);
    } catch (e) {
      setSpinning(false);
      toast.error(e.message || 'The wheel refused. Try again shortly.');
      if (e.status === 429) reload();
    }
  };

  const conicSegments = prizes.map((p, i) => {
    const startDeg = (i * 360) / prizes.length;
    const endDeg = ((i + 1) * 360) / prizes.length;
    return `${p.color || '#3a3f44'} ${startDeg}deg ${endDeg}deg`;
  }).join(', ');

  const canSpin = !!status?.can_spin;

  return (
    <section className="spin-page relative overflow-hidden pt-8 sm:pt-12 pb-16" data-testid="spin-page">
      {/* Ambient theatre */}
      <div aria-hidden className="blob b1" />
      <div aria-hidden className="blob b2" />
      <div aria-hidden className="blob b3" />
      <div aria-hidden className="grain" />
      <div aria-hidden className="scanlines" />
      <div aria-hidden className="grid-dots" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
        {/* HEADER */}
        <div className="text-center mb-8">
          <div className="eh-kicker justify-center mb-3 flex items-center gap-2">
            <Sparkles size={11} style={{ color: '#ffd34d' }} />
            <span>// THE OPERATORS&apos; ORACLE · DAILY</span>
            <Sparkles size={11} style={{ color: '#ffd34d' }} />
          </div>
          <h1 className="spin-title font-black leading-[0.94]" data-testid="spin-title">
            <span>spin the </span><span className="spin-gold">gilded</span><span> wheel.</span>
          </h1>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base opacity-80 italic leading-relaxed" style={{ fontFamily: 'Playfair Display, Cinzel, serif' }}>
            &ldquo;{hook}&rdquo;
          </p>

          {/* Mystic separator */}
          <div className="mystic-strip mt-5" aria-hidden>
            {MYSTIC_STRIP.map((s, i) => <span key={i} style={{ animationDelay: `${i * 120}ms` }}>{s}</span>)}
          </div>
        </div>

        {/* PRIZE PREVIEW STRIP */}
        <div className="prize-strip mb-6" data-testid="spin-prize-preview">
          {prizes.map((p, i) => (
            <div
              key={p.id || i}
              className="prize-chip"
              style={{ '--c': p.color || '#00ff9d' }}
              title={p.type === 'nothing' ? p.label : `${p.wheel_label || p.label} · reveals ${p.label}`}
            >
              {p.wheel_label || p.label.replace(/^₹\s?/, '₹')}
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center gap-8">
          {/* WHEEL HOLDER */}
          <div className="relative" style={{ width: 'min(88vw, 440px)' }}>
            {/* Aura rings */}
            <div className="aura aura-1" aria-hidden />
            <div className="aura aura-2" aria-hidden />
            <div className="aura aura-3" aria-hidden />

            {/* Confetti burst on big win */}
            {burst && (
              <div className="burst" aria-hidden>
                {Array.from({ length: 22 }).map((_, i) => (
                  <span
                    key={i}
                    className="burst-piece"
                    style={{
                      '--angle': `${(i / 22) * 360}deg`,
                      '--delay': `${(i % 6) * 40}ms`,
                      '--color': ['#00ff9d', '#ff2d92', '#4de0ff', '#ffd34d', '#c084fc'][i % 5],
                    }}
                  />
                ))}
              </div>
            )}

            {/* Pointer */}
            <div className="pointer" aria-hidden>
              <div className="pointer-triangle" />
              <div className="pointer-shine" />
            </div>

            {/* Wheel */}
            <div
              className="aspect-square rounded-full relative overflow-hidden"
              style={{
                background: `conic-gradient(from -90deg, ${conicSegments})`,
                transform: `rotate(${rotation}rad)`,
                transition: spinning ? 'transform 3.5s cubic-bezier(0.17, 0.67, 0.21, 0.99)' : 'none',
                boxShadow: '0 0 80px rgba(255,211,77,.28), 0 0 40px rgba(0,255,157,.20), inset 0 0 30px rgba(0,0,0,.55)',
                border: '4px solid rgba(255,211,77,.5)',
              }}
              ref={wheelRef}
              data-testid="spin-wheel"
            >
              {prizes.map((p, i) => {
                const angle = (i + 0.5) * (360 / prizes.length) - 90;
                const displayLabel = p.wheel_label || p.label;
                return (
                  <div key={p.id} className="absolute top-1/2 left-1/2" style={{ transform: `translate(-50%, -50%) rotate(${angle}deg) translate(0, -38%)` }}>
                    <div className="eh-mono font-bold text-[11px] sm:text-xs text-black whitespace-nowrap tracking-[0.14em]" style={{ transform: 'rotate(90deg)', textShadow: '0 1px 2px rgba(255,255,255,.35)' }}>
                      {displayLabel}
                    </div>
                  </div>
                );
              })}

              {/* Center hub */}
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="hub">
                  <div className="hub-inner">
                    <Sparkles size={22} style={{ color: '#ffd34d' }} />
                  </div>
                  <span className="hub-ring hub-ring-1" aria-hidden />
                  <span className="hub-ring hub-ring-2" aria-hidden />
                </div>
              </div>
            </div>
          </div>

          {/* CTA + status */}
          <div className="text-center w-full max-w-md">
            {canSpin ? (
              <>
                <button
                  onClick={handleSpin}
                  disabled={spinning}
                  data-testid="spin-go-btn"
                  className={`spin-cta ${spinning ? 'spinning' : ''}`}
                >
                  <span className="spin-cta-inner">
                    {spinning ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} />}
                    {spinning ? 'SPINNING…' : 'SPIN THE WHEEL'}
                  </span>
                  <span className="spin-cta-shine" />
                </button>
                <div className="eh-mono text-[10px] tracking-widest opacity-60 mt-3">
                  · ONE SPIN EVERY {cfg.cooldown_hours} HOURS · JACKPOT ₹500 ·
                </div>
              </>
            ) : (
              <div className="cooldown-card" data-testid="spin-cooldown">
                <Clock size={22} style={{ color: '#ffd34d' }} className="mx-auto mb-2" />
                <div className="eh-mono text-[10px] tracking-widest opacity-70">// NEXT SPIN OPENS IN</div>
                <div className="cooldown-timer">{timeLeft || '…'}</div>
                <div className="eh-mono text-[11px] opacity-60 mt-1">the vault refills at dawn.</div>
              </div>
            )}

            {winning && (
              <div className="win-card" data-testid="spin-result">
                <div className="win-crown"><Crown size={20} /></div>
                <Trophy size={26} style={{ color: '#ffd34d' }} className="mx-auto mb-2" />
                {winning.wheel_label && winning.wheel_label !== winning.label && (
                  <div className="eh-mono text-[10px] tracking-[0.3em] opacity-70 mb-1">// {winning.wheel_label}</div>
                )}
                <div className="win-title">{winning.label}</div>
                {winning.type === 'credit' && winning.amount > 0 ? (
                  <div className="eh-mono text-[11px] opacity-80 mt-1">
                    signed &amp; sealed to your <Link to="/me/wallet" className="eh-neon underline inline-flex items-center gap-1"><WalletIcon size={10} /> wallet</Link>
                  </div>
                ) : (
                  <div className="eh-mono text-[11px] opacity-70 mt-1">the wheel remembers. return tomorrow.</div>
                )}
                <div className="win-stars" aria-hidden>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={10} className="star" style={{ animationDelay: `${i * 100}ms` }} />
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center gap-3 mt-5">
              <Link to="/me/wallet" className="eh-btn-ghost text-xs" data-testid="spin-wallet-link"><WalletIcon size={12} /> WALLET</Link>
              <Link to="/me" className="eh-btn-ghost text-xs">← ACCOUNT</Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .spin-page {
          background: radial-gradient(ellipse at 30% -10%, rgba(255,211,77,.10), transparent 60%),
                      radial-gradient(ellipse at 100% 30%, rgba(255,45,146,.07), transparent 55%),
                      radial-gradient(ellipse at 20% 100%, rgba(0,255,157,.07), transparent 55%);
          isolation: isolate;
        }

        .blob { position: absolute; border-radius: 9999px; filter: blur(90px); opacity: .28; z-index: -1; animation: sf 16s ease-in-out infinite; }
        .blob.b1 { width: 380px; height: 380px; background: #ffd34d; top: -100px; left: -80px; }
        .blob.b2 { width: 300px; height: 300px; background: #ff2d92; top: 30%; right: -80px; animation-delay: -5s; }
        .blob.b3 { width: 260px; height: 260px; background: #00ff9d; bottom: -80px; left: 40%; animation-delay: -10s; }
        @keyframes sf { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(24px,-18px) scale(1.06); } }

        .grain {
          position: absolute; inset: 0; pointer-events: none; opacity: .07; z-index: -1;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
        .scanlines {
          position: absolute; inset: 0; pointer-events: none; z-index: -1; opacity: .04;
          background-image: repeating-linear-gradient(0deg, transparent 0, transparent 2px, #ffd34d 3px, transparent 4px);
        }
        .grid-dots {
          position: absolute; inset: 0; pointer-events: none; z-index: -1; opacity: .06;
          background-image: radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px);
          background-size: 22px 22px;
          mask-image: radial-gradient(ellipse at center, black 40%, transparent 80%);
        }

        .spin-title {
          font-family: 'Cinzel', 'Playfair Display', serif;
          font-size: clamp(2.2rem, 7vw, 4.4rem);
          letter-spacing: -0.01em;
          background: linear-gradient(180deg, #ffffff 0%, #d5d5d5 55%, #7a7a7a 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }
        .spin-gold {
          background: linear-gradient(180deg, #ffe999 0%, #ffd34d 50%, #b98a10 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
          filter: drop-shadow(0 2px 12px rgba(255,211,77,.35));
        }

        /* Mystic separator */
        .mystic-strip {
          display: flex; gap: 12px; justify-content: center; align-items: center;
          font-family: 'JetBrains Mono', monospace; color: rgba(255,211,77,.4); font-size: 11px;
        }
        .mystic-strip span { animation: pulseGlyph 3.5s ease-in-out infinite; }
        @keyframes pulseGlyph { 0%,100% { opacity: .25; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }

        /* Prize preview strip */
        .prize-strip {
          display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;
        }
        .prize-chip {
          padding: 5px 12px; border-radius: 999px;
          background: color-mix(in srgb, var(--c) 12%, transparent);
          border: 1px solid color-mix(in srgb, var(--c) 40%, transparent);
          color: var(--c);
          font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700;
          letter-spacing: 0.08em;
        }

        /* Aura rings */
        .aura {
          position: absolute; inset: -22px; border-radius: 9999px; pointer-events: none;
        }
        .aura-1 {
          border: 1.5px dashed rgba(255,211,77,.35);
          animation: spinCW 42s linear infinite;
        }
        .aura-2 {
          inset: -44px; border: 1px dashed rgba(0,255,157,.24);
          animation: spinCCW 62s linear infinite;
        }
        .aura-3 {
          inset: -66px; border: 1px solid rgba(77,224,255,.18);
          animation: pulseAura 4s ease-in-out infinite;
        }
        @keyframes spinCW { to { transform: rotate(360deg); } }
        @keyframes spinCCW { to { transform: rotate(-360deg); } }
        @keyframes pulseAura { 0%,100% { transform: scale(1); opacity: .8; } 50% { transform: scale(1.03); opacity: 1; } }

        /* Pointer */
        .pointer {
          position: absolute; left: 50%; top: -14px; z-index: 15; transform: translateX(-50%);
          filter: drop-shadow(0 4px 14px rgba(255,211,77,.55));
        }
        .pointer-triangle {
          width: 0; height: 0;
          border-left: 16px solid transparent; border-right: 16px solid transparent;
          border-top: 32px solid #ffd34d;
        }
        .pointer-shine {
          position: absolute; left: 50%; top: 6px; transform: translateX(-50%);
          width: 6px; height: 12px; border-radius: 2px;
          background: linear-gradient(180deg, rgba(255,255,255,.9), transparent);
          opacity: .8;
        }

        /* Hub */
        .hub {
          position: relative; width: 74px; height: 74px;
        }
        .hub-inner {
          width: 100%; height: 100%; border-radius: 9999px;
          background: radial-gradient(circle at 40% 30%, #2a1c05, #050301);
          border: 2px solid #ffd34d;
          box-shadow: 0 0 24px rgba(255,211,77,.65), inset 0 0 12px rgba(255,211,77,.3);
          display: grid; place-items: center;
        }
        .hub-ring {
          position: absolute; inset: -8px; border-radius: 9999px;
          border: 1px solid rgba(255,211,77,.45);
          animation: hubRing 2.4s ease-out infinite;
        }
        .hub-ring-2 { animation-delay: 1.2s; }
        @keyframes hubRing {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        /* Confetti burst */
        .burst { position: absolute; inset: 0; pointer-events: none; z-index: 20; }
        .burst-piece {
          position: absolute; top: 50%; left: 50%; width: 8px; height: 12px;
          background: var(--color); transform-origin: 50% 50%;
          border-radius: 2px;
          animation: burstFly 1.8s cubic-bezier(.2,.7,.3,1) var(--delay) both;
        }
        @keyframes burstFly {
          0%   { transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0) scale(0); opacity: 1; }
          20%  { transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-40px) scale(1); }
          100% { transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-220px) scale(.4); opacity: 0; }
        }

        /* CTA button */
        .spin-cta {
          position: relative; overflow: hidden;
          display: inline-flex; align-items: center; justify-content: center;
          padding: 16px 40px; min-width: 260px;
          background: linear-gradient(180deg, #ffe999, #ffd34d 50%, #d29a10);
          color: #0a0500;
          font-family: 'Cinzel', serif; font-weight: 900; font-size: 16px;
          letter-spacing: 0.18em; text-transform: uppercase;
          border-radius: 12px; border: 1px solid rgba(255,211,77,.6);
          box-shadow: 0 12px 40px -10px rgba(255,211,77,.6),
                      inset 0 1px 0 rgba(255,255,255,.5);
          cursor: pointer;
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .spin-cta:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 18px 50px -12px rgba(255,211,77,.75), inset 0 1px 0 rgba(255,255,255,.6);
        }
        .spin-cta:disabled { opacity: .8; cursor: wait; }
        .spin-cta-inner { display: inline-flex; align-items: center; gap: 10px; position: relative; z-index: 2; }
        .spin-cta-shine {
          position: absolute; inset: 0; z-index: 1;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.55) 50%, transparent 70%);
          animation: shine 3s linear infinite;
          transform: translateX(-100%);
        }
        @keyframes shine { to { transform: translateX(100%); } }

        /* Cooldown */
        .cooldown-card {
          padding: 22px; border-radius: 16px;
          background: linear-gradient(160deg, rgba(255,211,77,.06), transparent 65%);
          border: 1px solid rgba(255,211,77,.35);
          text-align: center;
        }
        .cooldown-timer {
          font-family: 'Cinzel', serif; font-weight: 900;
          font-size: clamp(1.6rem, 5vw, 2.4rem);
          color: #ffd34d; letter-spacing: 0.06em;
          margin-top: 4px;
          text-shadow: 0 2px 20px rgba(255,211,77,.35);
        }

        /* Win card */
        .win-card {
          position: relative; margin-top: 20px; padding: 22px 20px 26px;
          border-radius: 18px;
          background: linear-gradient(160deg, rgba(255,211,77,.10), rgba(0,255,157,.05) 60%);
          border: 1px solid rgba(255,211,77,.45);
          box-shadow: 0 20px 60px -20px rgba(255,211,77,.45);
        }
        .win-crown {
          position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
          width: 32px; height: 32px; border-radius: 9999px;
          background: #ffd34d; color: #0a0500; display: grid; place-items: center;
          box-shadow: 0 6px 20px -4px rgba(255,211,77,.7);
        }
        .win-title {
          font-family: 'Cinzel', serif; font-weight: 900; font-size: 22px;
          color: #ffd34d; letter-spacing: 0.06em;
          text-shadow: 0 2px 16px rgba(255,211,77,.4);
        }
        .win-stars { margin-top: 10px; display: flex; gap: 6px; justify-content: center; color: #ffd34d; }
        .win-stars .star { animation: starPop .6s cubic-bezier(.2,.9,.3,1) forwards; opacity: 0; transform: scale(0); fill: #ffd34d; }
        @keyframes starPop { to { opacity: 1; transform: scale(1); } }

        @media (prefers-reduced-motion: reduce) {
          .aura-1, .aura-2, .aura-3, .hub-ring, .blob, .mystic-strip span, .burst-piece, .spin-cta-shine { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default SpinWheel;
